import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import i18nMessages from '../../../constants/messages/pt-BR';
import { hideElementsBySelectors, restoreContentForScreenReaders, handleFocusTrap } from '../../../utils/ariaHiddenHelper';

class Modal extends Component {
  constructor(props) {
    super(props);
    this.modalRef = React.createRef();
    this.previouslyFocusedElement = null;
    this.hiddenElements = [];
  }

  componentDidMount() {
    const { open } = this.props;
    if (open) {
      this.handleOpen();
    }
  }

  componentDidUpdate(prevProps) {
    const { open } = this.props;
    
    // Handle open state changes
    if (open !== prevProps.open) {
      if (open) {
        this.handleOpen();
      } else {
        this.handleClose();
      }
    }
  }

  componentWillUnmount() {
    const { open } = this.props;
    if (open) {
      this.handleClose();
    }
  }

  handleOpen = () => {
    // Store the element that had focus before modal opens
    this.previouslyFocusedElement = document.activeElement;
    
    document.body.classList.add(
      i18nMessages.overflowhidden,
      i18nMessages.opacity85
    );
    document.addEventListener(
      i18nMessages.click,
      this.handleOutsideClick,
      false
    );
    document.addEventListener(i18nMessages.keydown, this.handleDocumentKeyDown);
    
    // Hide content from screen readers when modal opens
    this.hiddenElements = hideElementsBySelectors([
      '#page-header',
      '#content',
      '#page-footer'
    ]);
    
    // Focus the modal container so screen readers announce it
    if (this.modalRef.current) {
      // Focus the first focusable element in the modal, or the modal itself
      const focusableElements = this.modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        setTimeout(() => focusableElements[0].focus(), 50);
      } else {
        this.modalRef.current.focus();
      }
    }
  };

  handleClose = () => {
    document
      .querySelector(i18nMessages.body)
      .classList.remove(i18nMessages.overflowhidden, i18nMessages.opacity85);
    window.removeEventListener(
      i18nMessages.keydown,
      this.handleDocumentKeyDown
    );
    restoreContentForScreenReaders(this.hiddenElements);
    this.hiddenElements = [];
    
    document.removeEventListener(
      i18nMessages.click,
      this.handleOutsideClick,
      false
    );
    
    // Restore focus to the element that had focus before modal opened
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      this.previouslyFocusedElement.focus();
    }
  };

  handleDocumentKeyDown = event => {
    const { onEscapeKeyDown, onClose, disableEscapeKeyDown } = this.props;
    
    // Handle Tab key for focus trap
    if (event.key === 'Tab' && this.modalRef.current) {
      handleFocusTrap(this.modalRef.current, event);
    }
    
    // Handle Escape key
    if (event.key !== i18nMessages.Escape) {
      return;
    }
    if (onEscapeKeyDown) {
      onEscapeKeyDown(event);
    }

    if (!disableEscapeKeyDown && onClose) {
      onClose(event, i18nMessages.escapeKeyDown);
    }
  };

  handleBackdropClick = event => {
    const { onBackdropClick, disableBackdropClick, onClose } = this.props;
    if (event.target !== event.currentTarget) {
      return;
    }

    if (onBackdropClick) {
      onBackdropClick(event);
    }

    if (!disableBackdropClick && onClose) {
      onClose(event, i18nMessages.backdropClick);
    }
  };

  render() {
    const { backdropStyle, modalStyle, children, role } = this.props;
    const ariaLabelledby = this.props['aria-labelledby'];
    const ariaDescribedby = this.props['aria-describedby'];

    const modalContent = (
      <div
        role="presentation"
        onKeyPress={this.handleBackdropClick}
        className={backdropStyle}
        onClick={this.handleBackdropClick}
      >
        <div 
          ref={this.modalRef}
          role={role}
          aria-modal="true"
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
          tabIndex="-1"
          className={modalStyle}
        >
          {children}
        </div>
      </div>
    );

    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(modalContent, document.body);
    }
    return modalContent;
  }
}

Modal.propTypes = {
  open: PropTypes.bool.isRequired,
  onEscapeKeyDown: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onBackdropClick: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  modalStyle: PropTypes.string,
  backdropStyle: PropTypes.string,
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  onClose: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  role: PropTypes.string,
  'aria-labelledby': PropTypes.string,
  'aria-describedby': PropTypes.string,
};

Modal.defaultProps = {
  onEscapeKeyDown: null,
  onBackdropClick: null,
  modalStyle: '',
  backdropStyle: '',
  disableBackdropClick: false,
  disableEscapeKeyDown: false,
  onClose: null,
  role: 'dialog',
};

export default Modal;
